import { $, must } from './dom.ts';
import type { AssignmentConfig, Message, SiteMetadata, TestResults } from './evaluator-types.ts';
import { describe, getText } from './fetch-helpers.ts';

/*
 * Evaluator that runs Python test cases entirely in the browser using Pyodide
 * (CPython compiled to WebAssembly), hosted in a Web Worker (python-worker.ts).
 *
 * It mirrors the contract of the JavaScript and server evaluators: a single
 * `execute(code, filename)` method that resolves with a results map shaped for
 * the generic-testing display module, i.e.
 *
 *   { functionName: [ { label, got, expected, passed }, ... ] | null, ... }
 *
 * Unlike the JavaScript evaluator (which runs in an iframe on the main thread)
 * the Python runtime lives in a worker so a student infinite loop can be killed
 * by terminating the worker, and the one-time multi-megabyte Pyodide download
 * doesn't block the UI.
 */

const DEFAULT_TIMEOUT_MS = 10000;

interface Pending {
  resolve: (results: TestResults) => void;
  timer: ReturnType<typeof setTimeout>;
}

// Messages from python-worker.ts (matched to its post() calls).
type WorkerMessage =
  | { type: 'ready' }
  | { type: 'result'; id: number; results?: TestResults }
  | { type: 'error'; id: number; errorType: string; message: string };

class PythonEvaluator {
  config: AssignmentConfig;
  message: Message;
  stderr: HTMLElement | null;
  timeoutMs: number;
  worker: Worker | null;
  ready: boolean;
  nextId: number;
  pending: Map<number, Pending>;

  constructor(
    config: AssignmentConfig,
    message: Message,
    _username: string,
    _metadata: SiteMetadata,
  ) {
    this.config = config;
    this.message = message;
    // Most testcases pages have no #stderr; surface errors there when present.
    this.stderr = $('#stderr') as HTMLElement | null;
    this.timeoutMs = config.timeout ?? DEFAULT_TIMEOUT_MS;
    this.worker = null;
    this.ready = false;
    this.nextId = 0;
    this.pending = new Map();

    // Start the worker (and thus the Pyodide download) now so it warms up while
    // the student reads the problem.
    this.startWorker();
  }

  startWorker() {
    this.ready = false;
    const w = new Worker('/js/python-worker.js', { type: 'module' });
    w.onmessage = (e: MessageEvent) => this.onWorkerMessage(e.data);
    w.onerror = (e: ErrorEvent) => {
      console.log('Python worker error', e);
      this.message('Python failed to load.', undefined, 'error');
    };
    this.worker = w;
    this.message('Loading Python…');
  }

  /*
   * Tear down the current worker (e.g. after a timeout) and start a fresh one.
   * Any in-flight runs are resolved with no results so the UI clears cleanly.
   */
  restartWorker() {
    if (this.worker) this.worker.terminate();
    this.pending.forEach((entry) => {
      clearTimeout(entry.timer);
      entry.resolve(Object.create(null));
    });
    this.pending.clear();
    this.startWorker();
  }

  onWorkerMessage(msg: WorkerMessage) {
    if (msg?.type === 'ready') {
      this.ready = true;
      this.message('Python ready.', 1500);
      return;
    }

    const entry = this.pending.get(msg.id);
    if (!entry) return; // already timed out / unknown id
    clearTimeout(entry.timer);
    this.pending.delete(msg.id);

    if (msg.type === 'result') {
      entry.resolve(msg.results ?? Object.create(null));
    } else {
      this.showError(msg.errorType, msg.message);
      // Resolve with no results so the pills clear rather than leaving the
      // page with an unhandled rejection (index.ts doesn't .catch()).
      entry.resolve(Object.create(null));
    }
  }

  showError(errorType: string, detail: string) {
    this.message(errorType === 'compile' ? 'Python error.' : 'Runtime error.', undefined, 'error');
    if (this.stderr) {
      this.stderr.replaceChildren($('<pre>', detail) as HTMLElement);
    }
  }

  // A missing extra source is a visible "Couldn't load <file> (404)", never
  // a 404 page handed to Python as source.
  async #fetchExtraSources(): Promise<string[]> {
    const files: string[] = this.config.extraFiles ?? [];
    return Promise.all(
      files.map((f) =>
        getText(new URL(f, window.location.href)).catch((e) => {
          throw new Error(describe(e, `load ${f}`));
        }),
      ),
    );
  }

  async execute(
    code: string,
    filename: string,
    others: { file: string; content: string }[] = [],
  ): Promise<TestResults> {
    this.stderr?.replaceChildren();
    const extraSources = await this.#fetchExtraSources();
    const id = this.nextId++;
    this.message('Running Python…');

    return new Promise<TestResults>((resolve) => {
      const timer = setTimeout(() => {
        this.pending.delete(id);
        this.restartWorker();
        this.message('Time limit exceeded.', undefined, 'error');
        resolve(Object.create(null));
      }, this.timeoutMs);

      this.pending.set(id, { resolve, timer });
      must(this.worker, 'python worker').postMessage({
        type: 'run',
        id,
        studentCode: code,
        studentFilename: filename,
        extraSources,
        // The assignment's other editor tabs, written into Pyodide's
        // filesystem so the student's code can import them as modules.
        otherFiles: Object.fromEntries(others.map((o) => [o.file, o.content])),
      });
    });
  }
}

const evaluator = (
  config: AssignmentConfig,
  message: Message,
  username: string,
  metadata: SiteMetadata,
) => new PythonEvaluator(config, message, username, metadata);

export default evaluator;
